import React from 'react';
import { NavLink } from 'react-router-dom';
import { Home, Globe } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useData } from '../contexts/DataContext';

// The 3 main categories always appear in this fixed order, regardless of how the
// categories list happens to be sorted elsewhere. Matched by keyword rather than an
// exact slug since admins can rename a category's slug from the categories admin page
// (e.g. Silver's live slug is "silver-ornaments", not "silver").
const MAIN_CATEGORY_MATCHERS = [
  { slug: 'mesbah', en: 'mesbah', ar: 'مسبح' },
  { slug: 'ring', en: 'ring', ar: 'خاتم' },
  { slug: 'silver', en: 'silver', ar: 'فضة' },
];

// Tasbih (prayer beads) drawn as a loop of small beads around a thin string, with a
// pendant bead hanging below — same stroke weight/size as the Home/Globe icons beside it.
const MesbahIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" {...props}>
    <circle cx="12" cy="10" r="7" stroke="currentColor" strokeWidth="1" opacity="0.5" />
    {[[12, 3], [16.95, 5.05], [19, 10], [16.95, 14.95], [7.05, 14.95], [5, 10], [7.05, 5.05]].map(([cx, cy]) => (
      <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="1.3" fill="currentColor" />
    ))}
    <line x1="12" y1="17" x2="12" y2="21" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
    <circle cx="12" cy="22" r="1" fill="currentColor" />
  </svg>
);

// A simple solitaire ring silhouette — round band with a faceted stone on top.
const RingIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" {...props}>
    <circle cx="12" cy="16" r="5" stroke="currentColor" strokeWidth="1.75" />
    <path d="M12 5.5 L15.5 9.5 L12 13.5 L8.5 9.5 Z" stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round" />
  </svg>
);

// A draping chain necklace with a gem pendant hanging at its lowest point — for Silver
// ornaments (chains/pendants), same visual language as the other two icons (a loop/strand
// shape plus a hanging pendant detail).
const ChainIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M4 6 C4 13 8 16 12 16 C16 16 20 13 20 6" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
    <path d="M12 16 L14.2 18.5 L12 21.5 L9.8 18.5 Z" stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round" />
  </svg>
);

// Icons for the 3 fixed category tabs, matched positionally to MAIN_CATEGORY_MATCHERS above.
const CATEGORY_ICONS = { mesbah: MesbahIcon, ring: RingIcon, silver: ChainIcon };

// Glassy mobile-only bottom bar: quick access to the 3 main categories and
// account, plus a language switch, without needing to open the hamburger menu.
const MobileBottomBar = () => {
  const { lang, setLang } = useLanguage();
  const { categories: CATEGORIES } = useData();

  const mainCategories = MAIN_CATEGORY_MATCHERS
    .map((m) => {
      const cat = CATEGORIES.find((c) => !c.parentId && (
        c.slug === m.slug || (c.nameEn || '').toLowerCase().includes(m.en) || (c.nameAr || '').includes(m.ar)
      ));
      return cat ? { ...cat, iconKey: m.slug } : null;
    })
    .filter(Boolean);

  // Each tab is an oval pill that fills in with the brand black when active, instead of
  // just a text-color change — matches the site's yellow/gold accent theme.
  const tabPill = (isActive) =>
    `flex flex-col items-center justify-center gap-0.5 px-4 py-1.5 rounded-full text-[11px] font-semibold transition-colors ${
      isActive ? 'bg-[#171412] text-white' : 'text-[#171412]/70'
    }`;

  return (
    <nav
      className="md:hidden fixed bottom-0 inset-x-0 z-40 border-t border-white/40 bg-gold-400/90 backdrop-blur-2xl backdrop-saturate-150 shadow-[0_-8px_32px_rgba(0,0,0,0.15)]"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div className="flex items-stretch justify-around py-1.5">
        <NavLink to="/" end className="flex items-center justify-center">
          {({ isActive }) => (
            <span className={tabPill(isActive)}>
              <Home className="w-5 h-5" strokeWidth={1.75} />
              {lang === 'ar' ? 'الرئيسية' : 'Home'}
            </span>
          )}
        </NavLink>

        {mainCategories.map((c) => {
          const Icon = CATEGORY_ICONS[c.iconKey];
          return (
            <NavLink key={c.id} to={`/category/${c.slug}`} className="flex items-center justify-center">
              {({ isActive }) => (
                <span className={tabPill(isActive)}>
                  <Icon className="w-5 h-5" strokeWidth={1.75} />
                  {lang === 'ar' ? c.nameAr : c.nameEn}
                </span>
              )}
            </NavLink>
          );
        })}

        <button type="button" onClick={() => setLang(lang === 'ar' ? 'en' : 'ar')} className={tabPill(false)}>
          <Globe className="w-5 h-5" strokeWidth={1.75} />
          {lang === 'ar' ? 'English' : 'العربية'}
        </button>
      </div>
    </nav>
  );
};

export default MobileBottomBar;
